import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { useAmplitude } from 'react-amplitude-hooks';
import InfoModal from 'Components/shared/Modal/InfoModal';
import { EnvContext } from 'States/env/envState';
import { ConfigContext } from 'States/config/configState';
import { LangContext } from 'States/lang/langState';
import { getTranslation } from 'Helpers/translations';
import useConfigSettings from 'Hooks/useConfigSettings';
import AssetCardButtonGroup from './AssetCardButtonGroup';
import AssetModal from './AssetModal';
import CustomCard from './CustomCard';
import { ROUTES } from 'Router/Routes';

const AssetCard = ({ assetConfig, dataQa, isCartModalOpen = false }) => {
  const history = useHistory();
  const { logEvent } = useAmplitude();
  const { env } = useContext(EnvContext);
  const {
    config: { kycTierFunctionalRestrictions }
  } = useContext(ConfigContext);
  const { lang } = useContext(LangContext);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const timerInterval = useRef(-1);
  const secondsOpen = useRef(0);

  const onFocus = () => {
    if (timerInterval.current !== -1) {
      logEvent(
        'Purchase details checked',
        { useful: secondsOpen.current >= 3 }
      );
      clearInterval(timerInterval.current);
      timerInterval.current = -1;
      secondsOpen.current = 0;
    }
  };

  // TODO: secondsOpen does not properly get read if eventListener isn't refreshed at every change.
  // Maybe there is a better way to send the correct seconds without reloading eventListener
  useEffect(() => {
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const {
    data: defaultLanguage
  } = useConfigSettings.query({
    select: useCallback(
      ({ Languages }) => {
        const defaultLanguage = Languages.find(({ IsDefault }) => IsDefault);
        return defaultLanguage;
      }, []
    )
  });

  const requiredTier = Math.max(
    kycTierFunctionalRestrictions?.availability?.purchase || 0,
    assetConfig.KycTier || 0
  );

  const translatedName = useMemo(() => {
    const name = getTranslation("Name", lang, defaultLanguage.Code, assetConfig.Asset.Translations);
    const ticker = getTranslation("Ticker", lang, defaultLanguage.Code, assetConfig.Asset.Translations);
    return ticker ? `${name} (${ticker})` : name;
  }, [lang]);

  const translatedDescription = useMemo(() => getTranslation(
    "Description", lang, defaultLanguage.Code, assetConfig.Asset.Translations
  ), [lang]);

  const {
    translatedAddress,
    translatedLink
  } = useMemo(() => {
    const translatedDefinitionItems = getTranslation(
      "CustomDefinitionItems", lang, defaultLanguage.Code, assetConfig.Asset.Translations
    );
    const addressDefinitionItem = translatedDefinitionItems.find(({ Name }) => Name === "Address");
    const linkDefinitionItem = translatedDefinitionItems.find(({ Name }) => Name === "Link");
    return {
      translatedAddress: addressDefinitionItem?.Value || "",
      translatedLink: linkDefinitionItem?.Value || ""
    };
  }, [lang]);

  const openAssetInfo = useCallback(() => {
    if (!translatedLink) {
      return;
    }
    if (true) {
      const timer = setInterval(
        () => secondsOpen.current = secondsOpen.current + 1,
        1000
      );
      timerInterval.current = timer;
      window.open(translatedLink, '_blank', 'noopener,noreferrer');
    } else {
      setShowInfoModal(true);
      logEvent('Purchase details checked');
    }
  }, []);

  const closeInfoModal = () => {
    setShowInfoModal(false);
  };

  const openAssetModal = useCallback(() => {
    history.replace(generatePath(ROUTES.shop, {
      assetId: assetConfig.Id
    }));
  }, []);

  const closeAssetModal = () => {
    history.replace(generatePath(ROUTES.shop));
  }

  return (
    <>
      <CustomCard
        dataQa={dataQa}
        name={translatedName}
        iconUrl={`${env.BlobUrl}/${assetConfig.Icon}`}
        description={translatedDescription}
        subtitle={translatedAddress}
        buttonGroup={() =>
          AssetCardButtonGroup({
            openAssetInfo,
            openAssetModal,
            kycTier: requiredTier,
          })
        }
      />
      {isCartModalOpen && (
        <AssetModal
          show={isCartModalOpen}
          close={closeAssetModal}
          assetConfig={{
            ...assetConfig,
            Name: translatedName,
            Description: translatedDescription
          }}
          defaultLanguage={defaultLanguage.Code}
        />
      )}
      {showInfoModal && (
        <InfoModal
          show={showInfoModal}
          close={closeInfoModal}
          content={{
            title: translatedName,
            description: translatedDescription,
            icon: `${env.BlobUrl}/${assetConfig.Icon}`
          }}
          dataQa="checkout-collectionMethod-infoModal"
        />
      )}
    </>
  );
};

export default AssetCard;
